import type {
  AdminAccountOption,
  AdminAccountProject,
  AdminPlanTemplate,
  AdminPlanTemplateInput,
} from '@brand24-original/llm-types';
import { client } from '../../apiClient';
import { UsersDataPageResponse } from '../../../models/admin/users/UsersDataPageResponse';
import { UserAccountData } from '../../../../pages/admin/users/edit/models/UserAccountData';
import { AccountDataResponse } from '../../../models/account/AccountData';
import { InvitedUserResponse } from '../../../models/admin/users/InvitedUserResponse';
import { UserTeamData } from '../../../models/team/TeamResponse';
import { TokenData } from '../../../models/auth/TokenData';
import { AdminNoteResponse } from '../../../models/admin/users/AdminNoteResponse';
import { AdminAuditLogEntry } from '../../../models/admin/audit/AdminAuditLog';
import { UserActivityLogEntry } from '../../../models/admin/audit/UserActivityLog';

export const getAuditLog = async (accountId: number): Promise<AdminAuditLogEntry[]> => {
  return client(false, true)
    .get<AdminAuditLogEntry[]>(`/admin/users/audit-log/${accountId}`)
    .then((response) => response.data)
    .catch(() => []);
};

export const getUserActivityLog = async (accountId: number): Promise<UserActivityLogEntry[]> => {
  return client(false, true)
    .get<UserActivityLogEntry[]>(`/admin/users/activity-log/${accountId}`)
    .then((response) => response.data)
    .catch(() => []);
};

export const getAccountOptions = async (): Promise<AdminAccountOption[]> => {
  return client(true, true)
    .get<AdminAccountOption[]>(`/admin/dashboard/account-options`)
    .then((response) => response.data);
};

export type BillingCycleFilter = 'all' | 'monthly' | 'annual';

export const getUsersPage = async (filters?: {
  search?: string;
  accountType?: string;
  accountStatus?: string;
  accountOrigin?: 'all' | 'organic' | 'cs_assisted' | 'cs_assisted_brand24' | `api_client_${number}`;
  isCancelled?: boolean;
  confirmed?: boolean;
  noProject?: boolean;
  billingInterval?: BillingCycleFilter;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
  /**
   * Dokłada do wiersza listę osób pod kontem i rozszerza wyszukiwanie o ich adresy. Domyślnie
   * wyłączone — potrzebuje tego wyłącznie lista kont w panelu admina.
   */
  withMembers?: boolean;
}): Promise<UsersDataPageResponse> => {
  const params = new URLSearchParams();
  params.set('page', String(filters?.page ?? 1));
  params.set('pageSize', String(filters?.pageSize ?? 50));
  if (filters?.search) params.set('search', filters.search);
  if (filters?.accountType) params.set('accountType', filters.accountType);
  if (filters?.accountStatus) params.set('accountStatus', filters.accountStatus);
  if (filters?.accountOrigin && filters.accountOrigin !== 'all')
    params.set('accountOrigin', filters.accountOrigin);
  if (filters?.isCancelled !== undefined) params.set('isCancelled', String(filters.isCancelled));
  if (filters?.confirmed !== undefined) params.set('confirmed', String(filters.confirmed));
  if (filters?.noProject === true) params.set('noProject', 'true');
  if (filters?.billingInterval && filters.billingInterval !== 'all')
    params.set('billingInterval', filters.billingInterval);
  if (filters?.sortBy) params.set('sortBy', filters.sortBy);
  if (filters?.sortDir) params.set('sortDir', filters.sortDir);
  if (filters?.withMembers) params.set('withMembers', 'true');

  return client(true, true)
    .get<UsersDataPageResponse>(`/admin/dashboard/all-users-page?${params}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const addAdminNote = async (accountId: number, content: string): Promise<AdminNoteResponse> => {
  return client(false, true)
    .post<AdminNoteResponse>(`/admin/users/admin-note/${accountId}`, { content })
    .then((response) => response.data);
};

export const updateAdminNote = async (
  accountId: number,
  noteId: number,
  content: string
): Promise<AdminNoteResponse> => {
  return client(false, true)
    .patch<AdminNoteResponse>(`/admin/users/admin-note/${accountId}/${noteId}`, { content })
    .then((response) => response.data);
};

export const deleteAdminNote = async (
  accountId: number,
  noteId: number
): Promise<{ message: string }> => {
  return client(false, true)
    .delete<{ message: string }>(`/admin/users/admin-note/${accountId}/${noteId}`)
    .then((response) => response.data);
};

export const registerUser = async (formData: URLSearchParams): Promise<any> => {
  return client(false, true).post<any>('/admin/users/register-user', formData);
};

export const getAccountData = async (id: number): Promise<AccountDataResponse> => {
  return client(false, true)
    .get<AccountDataResponse>(`/admin/users/account-data/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getProjectsForAccount = async (id: number): Promise<AdminAccountProject[]> => {
  return client(false, true)
    .get<AdminAccountProject[]>(`/admin/users/projects/${id}`)
    .then((response) => response.data)
    .catch(() => []);
};

export const getInvitedUsers = async (id: number): Promise<InvitedUserResponse[]> => {
  return client(false, true)
    .get<InvitedUserResponse[]>(`/admin/users/invited-users/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const changeUserAccountData = async (userAccount: UserAccountData): Promise<any> => {
  return client(false, true).post<any>('/admin/users/account-data', userAccount);
};

/** Re-pins every live project of the account to its saved monitoring frequency. */
export const syncMonitoringSchedule = async (accountId: number): Promise<{ projectsSynced: number }> => {
  return client(false, true)
    .post<{ projectsSynced: number }>(`/admin/users/sync-monitoring-schedule/${accountId}`)
    .then((response) => response.data);
};

export const inviteUserByAdmin = async (
  id: number,
  userTeamData: UserTeamData
): Promise<InvitedUserResponse[]> => {
  return client(false, true)
    .post<InvitedUserResponse[]>(`/admin/users/invite-user/${id}`, userTeamData)
    .then((response) => response.data)
    .catch((error) => error);
};

export const resetPasswordByAdmin = async (email: string): Promise<{ message: string }> => {
  return client(false, true)
    .post<{ message: string }>(`/admin/users/reset-password`, { email })
    .then((response) => response.data);
};

// Plan templates: named model + addon sets support applies from the account page.
export const getPlanTemplates = async (): Promise<AdminPlanTemplate[]> => {
  return client(false, true)
    .get<AdminPlanTemplate[]>('/admin/users/plan-templates')
    .then((response) => response.data)
    .catch(() => []);
};

export const createPlanTemplate = async (input: AdminPlanTemplateInput): Promise<AdminPlanTemplate> => {
  return client(false, true)
    .post<AdminPlanTemplate>('/admin/users/plan-templates', input)
    .then((response) => response.data);
};

export const updatePlanTemplate = async (
  id: number,
  input: AdminPlanTemplateInput
): Promise<AdminPlanTemplate> => {
  return client(false, true)
    .put<AdminPlanTemplate>(`/admin/users/plan-templates/${id}`, input)
    .then((response) => response.data);
};

export const deletePlanTemplate = async (id: number): Promise<void> => {
  await client(false, true).delete(`/admin/users/plan-templates/${id}`);
};

export const banAccount = async (accountId: number): Promise<void> => {
  await client(true, true).post(`/admin/users/ban/${accountId}`);
};

export const unbanAccount = async (accountId: number): Promise<void> => {
  await client(true, true).post(`/admin/users/unban/${accountId}`);
};

/** Pushes a temporary (automatic) ban's end 24 h further. 409 when the ban is not temporary. */
export const extendBan = async (accountId: number, hours = 24): Promise<{ bannedUntil: string }> => {
  return client(true, true)
    .post<{ bannedUntil: string }>(`/admin/users/ban/${accountId}/extend`, { hours })
    .then((response) => response.data);
};

export const impersonateUser = async (userId: number): Promise<TokenData> => {
  return client(true, true)
    .post<TokenData>(`/admin/users/impersonate/${userId}`)
    .then((response) => response.data);
};

export const getApiClients = async (filters?: {
  search?: string;
  accountType?: string;
  accountStatus?: string;
  isCancelled?: boolean;
}): Promise<{ id: number; name: string; accountCount: number }[]> => {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.accountType) params.set('accountType', filters.accountType);
  if (filters?.accountStatus) params.set('accountStatus', filters.accountStatus);
  if (filters?.isCancelled !== undefined) params.set('isCancelled', String(filters.isCancelled));
  const qs = params.toString();
  return client(true, true)
    .get<{ id: number; name: string; accountCount: number }[]>(
      `/admin/dashboard/api-clients${qs ? `?${qs}` : ''}`
    )
    .then((response) => response.data)
    .catch(() => []);
};
